/**
 * Request Deduplication System
 * 
 * Prevents duplicate requests from:
 * - Multiple rapid button clicks
 * - Network retries
 * - Race conditions in UI
 * 
 * Uses request fingerprinting to detect and block duplicate requests
 */

class RequestDeduplicator {
  constructor(defaultWindow = 5000) {
    this.activeRequests = new Map();
    this.completedRequests = new Map();
    this.defaultWindow = defaultWindow; // 5 seconds default
    
    // Cleanup completed requests periodically
    setInterval(() => this.cleanup(), 30000); // Every 30 seconds
  }

  /**
   * Create fingerprint for request
   * @param {string} operation - Operation name (e.g., 'purchase', 'list', 'offer')
   * @param {Object} params - Request parameters
   * @returns {string} Unique fingerprint
   */
  createFingerprint(operation, params) {
    // Sort keys for consistent fingerprinting
    const sortedParams = Object.keys(params || {})
      .sort()
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {});

    return `${operation}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Execute request with deduplication
   * @param {string} operation - Operation name
   * @param {Function} requestFn - Async function to execute
   * @param {Object} params - Request parameters for fingerprinting
   * @param {Object} options - Deduplication options
   * @returns {Promise} Result of request
   */
  async execute(operation, requestFn, params = {}, options = {}) {
    const {
      window = this.defaultWindow,
      allowRetry = false,
      returnCached = true
    } = options;

    const fingerprint = this.createFingerprint(operation, params);

    // Check if request is already in progress
    const activeRequest = this.activeRequests.get(fingerprint);
    if (activeRequest) {
      console.log(`🔄 Duplicate request detected (active): ${operation}`);
      
      // Return the same promise to avoid duplicate execution
      return activeRequest.promise;
    }

    // Check if request was recently completed
    const completedRequest = this.completedRequests.get(fingerprint);
    if (completedRequest && returnCached) {
      const age = Date.now() - completedRequest.timestamp;
      if (age < window) {
        console.log(`✅ Returning cached result: ${operation} (${age}ms ago)`);
        
        if (completedRequest.success) {
          return completedRequest.result;
        } else if (!allowRetry) {
          throw completedRequest.error;
        }
      }
    }

    // Create new request
    const requestData = {
      fingerprint,
      operation,
      startTime: Date.now(),
      promise: null
    };

    // Execute request
    requestData.promise = (async () => {
      try {
        console.log(`🚀 Executing request: ${operation}`);
        const result = await requestFn();
        
        // Cache successful result
        this.completedRequests.set(fingerprint, {
          success: true,
          result,
          timestamp: Date.now()
        });

        return result;
      } catch (error) {
        // Cache error (can be retried if allowRetry is true)
        this.completedRequests.set(fingerprint, {
          success: false,
          error,
          timestamp: Date.now()
        });

        throw error;
      } finally {
        // Remove from active requests
        this.activeRequests.delete(fingerprint);
      }
    })();

    // Store active request
    this.activeRequests.set(fingerprint, requestData);

    return requestData.promise;
  }

  /**
   * Clear cache for specific operation/params
   * Useful when you know state has changed
   */
  invalidate(operation, params = null) {
    if (params === null) {
      // Clear all requests for this operation
      let cleared = 0;
      for (const [fingerprint] of this.completedRequests.entries()) {
        if (fingerprint.startsWith(`${operation}:`)) {
          this.completedRequests.delete(fingerprint);
          cleared++;
        }
      }
      console.log(`🗑️ Cleared ${cleared} cached requests for: ${operation}`);
    } else {
      // Clear specific request
      const fingerprint = this.createFingerprint(operation, params);
      this.completedRequests.delete(fingerprint);
      console.log(`🗑️ Cleared cached request: ${operation}`);
    }
  }

  /**
   * Clear all active and completed requests
   */
  clearAll() {
    this.activeRequests.clear();
    this.completedRequests.clear();
    console.log('🗑️ Cleared all request caches');
  }

  /**
   * Cleanup old completed requests
   */
  cleanup(maxAge = 300000) { // 5 minutes default
    const now = Date.now();
    let cleaned = 0;

    for (const [fingerprint, request] of this.completedRequests.entries()) {
      if (now - request.timestamp > maxAge) {
        this.completedRequests.delete(fingerprint);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      console.log(`🧹 Cleaned up ${cleaned} old requests`);
    }
  }

  /**
   * Get deduplication stats
   */
  getStats() {
    return {
      activeRequests: this.activeRequests.size,
      cachedRequests: this.completedRequests.size,
      activOperations: Array.from(this.activeRequests.values()).map(r => r.operation)
    };
  }
}

// Create singleton instance
const deduplicator = new RequestDeduplicator();

export default deduplicator;

/**
 * Deduplicate a transaction request
 * Prevents duplicate blockchain transactions from rapid button clicks
 * 
 * @param {string} operation - Operation name (e.g., 'purchase-nft', 'list-nft')
 * @param {Function} requestFn - Async function that performs the transaction
 * @param {Object} params - Parameters that uniquely identify this request
 * @param {Object} options - Deduplication options
 * @returns {Promise} Result of the transaction
 * 
 * @example
 * const result = await deduplicateTransaction(
 *   'purchase-nft',
 *   () => executePurchase(mintAddress, price),
 *   { mintAddress, price },
 *   { window: 10000 } // 10 second deduplication window
 * );
 */
export async function deduplicateTransaction(operation, requestFn, params, options = {}) {
  return deduplicator.execute(operation, requestFn, params, {
    window: 10000, // 10 seconds for transactions
    allowRetry: false, // Don't retry failed transactions automatically
    returnCached: true,
    ...options
  });
}

/**
 * Deduplicate an API request
 * Prevents duplicate API calls from rapid UI interactions
 */
export async function deduplicateApiCall(operation, requestFn, params, options = {}) {
  return deduplicator.execute(operation, requestFn, params, {
    window: 2000, // 2 seconds for API calls
    allowRetry: true, // Can retry failed API calls
    returnCached: true,
    ...options
  });
}

/**
 * Invalidate cached requests
 * Call this when you know the state has changed (e.g., after successful transaction)
 */
export function invalidateCache(operation, params = null) {
  deduplicator.invalidate(operation, params);
}

/**
 * Clear all caches
 * Useful on wallet disconnect/reconnect
 */
export function clearAllCaches() {
  deduplicator.clearAll();
}

/**
 * Get deduplication statistics
 */
export function getDeduplicationStats() {
  return deduplicator.getStats();
}

/**
 * Higher-order function to wrap any function with deduplication
 * 
 * @example
 * const deduplicatedPurchase = withDeduplication(
 *   'purchase-nft',
 *   purchaseNft,
 *   (mintAddress, price) => ({ mintAddress, price })
 * );
 * 
 * // Use it like normal, but it will deduplicate
 * await deduplicatedPurchase(mintAddress, price);
 */
export function withDeduplication(operation, fn, paramsExtractor) {
  return async (...args) => {
    const params = paramsExtractor ? paramsExtractor(...args) : {};
    return deduplicator.execute(operation, () => fn(...args), params);
  };
}

